import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Plus } from "lucide-react";
import { useStore, formatPrice } from "../../context/StoreContext";

const ProductCard = ({ product, eager = false }) => {
  const { wishlist, toggleWishlist, currency, setQuickBuy } = useStore();
  const saved = wishlist.includes(product.id);
  const [activeIdx, setActiveIdx] = useState(0);
  const scrollerRef = useRef(null);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    setActiveIdx(Math.round(el.scrollLeft / w));
  };

  return (
    <div
      data-testid={`product-card-${product.id}`}
      className="group relative product-card flex flex-col"
    >
      {/* Media: rounded, soft shadow, hover lift */}
      <Link
        to={`/product/${product.id}`}
        className="relative block w-full overflow-hidden rounded-[20px] shadow-card group-hover:shadow-card-hover transition-shadow duration-300 bg-cream-soft"
        aria-label={product.name}
      >
        <div
          ref={scrollerRef}
          onScroll={onScroll}
          className="flex w-full overflow-x-auto snap-row hide-scrollbar"
          style={{ aspectRatio: "3 / 4" }}
        >
          {product.images.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={`${product.name} ${i + 1}`}
              loading={eager && i === 0 ? "eager" : "lazy"}
              draggable={false}
              className="w-full flex-shrink-0 object-cover h-full select-none"
              style={{ width: "100%" }}
            />
          ))}
        </div>

        {/* Category tag — pill, no hard rectangle */}
        <span
          className={`absolute top-3 left-3 font-mono text-[10px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full backdrop-blur ${
            product.category === "EXCLUSIVE"
              ? "bg-dc-red/95 text-cream"
              : "bg-ink/85 text-cream"
          }`}
        >
          {product.category}
        </span>

        {/* Sold out badge */}
        {product.stockQty === 0 && (
          <span className="absolute top-3 left-1/2 -translate-x-1/2 bg-cream/95 text-ink font-mono text-[10px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full shadow-float">
            SOLD OUT
          </span>
        )}

        {/* Dot indicators */}
        {product.images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 pointer-events-none">
            {product.images.map((src, i) => (
              <span
                key={src}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${
                  i === activeIdx ? "bg-cream" : "bg-cream/40"
                }`}
              />
            ))}
          </div>
        )}

        {/* Bookmark — minimal floating outlined icon, no box */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleWishlist(product.id);
          }}
          aria-label={saved ? "Remove from wishlist" : "Save to wishlist"}
          data-testid={`wishlist-toggle-${product.id}`}
          className="dc-float-chip absolute top-2.5 right-2.5"
        >
          <Bookmark
            size={20}
            strokeWidth={2}
            className={
              saved
                ? "fill-dc-red text-dc-red"
                : "text-cream"
            }
          />
        </button>
      </Link>

      {/* Bottom row — generous padding, hairline separator, two-line title */}
      <div className="flex items-start justify-between gap-3 px-1.5 pt-4 pb-2">
        <div className="min-w-0 flex-1">
          <Link
            to={`/product/${product.id}`}
            className="block font-display text-[15px] md:text-base leading-snug text-ink hover:text-dc-red transition-colors line-clamp-2 min-h-[2.6em]"
            title={product.name}
          >
            {product.name}
          </Link>
          <p
            data-testid={`price-${product.id}`}
            className="font-mono text-xs font-bold mt-1.5 text-ink/75"
          >
            {formatPrice(product.priceInr, currency)}
          </p>
        </div>
        <button
          onClick={(e) => {
            e.preventDefault();
            setQuickBuy(product);
          }}
          aria-label="Quick buy"
          data-testid={`quickbuy-open-${product.id}`}
          className="dc-add-circle flex-shrink-0 mt-0.5"
        >
          <Plus size={20} strokeWidth={2.25} />
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
